import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../../components/AdminLayout';
import { Card } from '../../styles/GlobalStyles';
import { clubService } from '../../services/clubService';
import { ClubContact, ClubRole, ClubInvitation, ClubInvitationStatus } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-lg);
  flex-wrap: wrap;
  gap: var(--spacing-md);
`;

const PageTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const InviteButtonGroup = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  flex-wrap: wrap;
`;

const InviteButton = styled.button`
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  min-height: 44px;

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 767px) {
    flex: 1;
    min-width: 140px;
  }
`;

const FilterSection = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const TabContainer = styled.div`
  display: flex;
  gap: var(--spacing-xs);
  margin-bottom: var(--spacing-md);
  overflow-x: auto;
  padding-bottom: var(--spacing-xs);
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  color: ${props => props.$active ? 'white' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  white-space: nowrap;
  min-height: 44px;

  &:hover {
    background-color: ${props => props.$active ? 'var(--primary-color)' : 'var(--hover-bg, #f5f5f5)'};
  }
`;

const SearchBar = styled.input`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const ContactsCard = styled(Card)`
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const ContactsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-md);
  padding-bottom: var(--spacing-md);
  border-bottom: 1px solid var(--border-color);
`;

const ContactsTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ContactCount = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const ContactsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const ContactItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  gap: var(--spacing-md);

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const ContactName = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1rem;
`;

const ContactEmail = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const ContactMeta = styled.div`
  color: var(--text-secondary);
  font-size: 0.875rem;
`;

const ContactRole = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: var(--primary-color)15;
  color: var(--primary-color);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RemoveButton = styled.button`
  padding: var(--spacing-sm) var(--spacing-md);
  background-color: transparent;
  color: var(--error-color, #c00);
  border: 1px solid var(--error-color, #c00);
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  min-height: 44px;

  &:hover {
    background-color: var(--error-color, #c00);
    color: white;
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

const EmptyState = styled.div`
  padding: var(--spacing-xl);
  text-align: center;
  color: var(--text-secondary);
`;

const ErrorMessage = styled.div`
  padding: var(--spacing-lg);
  background-color: var(--error-bg, #fee);
  color: var(--error-color, #c00);
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-lg);
`;

const Modal = styled.div<{ $show: boolean }>`
  display: ${props => props.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
`;

const ModalContent = styled.div`
  background-color: white;
  border-radius: var(--border-radius);
  padding: var(--spacing-xl);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  font-size: 0.875rem;
`;

const Input = styled.input`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  min-height: 44px;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }

  &:disabled {
    background-color: var(--disabled-bg, #f5f5f5);
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);
  font-size: 1rem;
  min-height: 44px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
  }
`;

const HelperText = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-top: var(--spacing-xs);
`;

const SuccessMessage = styled.div`
  padding: var(--spacing-md);
  background-color: #00800020;
  color: #008000;
  border-radius: var(--border-radius);
  margin-bottom: var(--spacing-md);
  font-size: 0.875rem;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: var(--spacing-sm);
  justify-content: flex-end;
  margin-top: var(--spacing-lg);
`;

const ModalButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: var(--spacing-sm) var(--spacing-lg);
  background-color: ${props => 
    props.$variant === 'danger' ? 'var(--error-color, #c00)' :
    props.$variant === 'primary' ? 'var(--primary-color)' : 
    'transparent'};
  color: ${props => 
    props.$variant === 'secondary' ? 'var(--text-primary)' : 
    'white'};
  border: ${props => 
    props.$variant === 'secondary' ? '1px solid var(--border-color)' : 
    'none'};
  border-radius: var(--border-radius);
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
  min-height: 44px;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PendingInvitationsSection = styled.div`
  margin-top: var(--spacing-xl);
`;

const InvitationItem = styled(ContactItem)`
  background-color: var(--primary-color)05;
`;

const InvitationStatus = styled.span<{ $status: ClubInvitationStatus }>`
  display: inline-block;
  padding: 2px 8px;
  background-color: ${props => 
    props.$status === ClubInvitationStatus.PENDING ? '#ffa50020' :
    props.$status === ClubInvitationStatus.ACCEPTED ? '#00800020' :
    props.$status === ClubInvitationStatus.EXPIRED ? '#80808020' :
    '#ff000020'};
  color: ${props => 
    props.$status === ClubInvitationStatus.PENDING ? '#ffa500' :
    props.$status === ClubInvitationStatus.ACCEPTED ? '#008000' :
    props.$status === ClubInvitationStatus.EXPIRED ? '#808080' :
    '#ff0000'};
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

type RoleFilter = 'ALL' | ClubRole;

export const AdminContactsPage: React.FC = () => {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<ClubContact[]>([]);
  const [invitations, setInvitations] = useState<ClubInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [contactToRemove, setContactToRemove] = useState<ClubContact | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteRole, setInviteRole] = useState<ClubRole>(ClubRole.COACH);
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    linkedPlayerId: ''
  });
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [players, setPlayers] = useState<any[]>([]);

  const fetchContacts = async () => {
    if (!user?.profile?.clubId) {
      setError('No club associated with your account');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const roleParam = roleFilter === 'ALL' ? undefined : roleFilter;
      const searchParam = searchTerm.trim() || undefined;
      
      const [contactsData, invitationsData] = await Promise.all([
        clubService.getContacts(user.profile.clubId, roleParam, searchParam),
        clubService.getInvitations(user.profile.clubId)
      ]);
      
      setContacts(contactsData);
      setInvitations(invitationsData.filter(inv => inv.status === ClubInvitationStatus.PENDING));
    } catch (err: any) {
      console.error('Failed to fetch contacts:', err);
      setError(err.response?.data?.error?.message || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile?.clubId, roleFilter, searchTerm]);

  const handleRemoveClick = (contact: ClubContact) => {
    setContactToRemove(contact);
    setShowRemoveModal(true);
  };

  const handleRemoveConfirm = async () => {
    if (!contactToRemove || !user?.profile?.clubId) return;

    try {
      await clubService.removeMember(user.profile.clubId, contactToRemove.userId);
      setShowRemoveModal(false);
      setContactToRemove(null);
      fetchContacts();
    } catch (err: any) {
      console.error('Failed to remove member:', err);
      setError(err.response?.data?.error?.message || 'Failed to remove member');
    }
  };

  const handleInviteClick = (role: ClubRole) => {
    setInviteRole(role);
    setInviteForm({ firstName: '', lastName: '', email: '', linkedPlayerId: '' });
    setInviteSuccess(false);
    setShowInviteModal(true);
    
    // Fetch players if inviting a parent
    if (role === ClubRole.PARENT && user?.profile?.clubId) {
      fetchPlayers();
    }
  };

  const fetchPlayers = async () => {
    if (!user?.profile?.clubId) return;
    
    try {
      const teams = await clubService.getClubTeams(user.profile.clubId);
      const allPlayers = teams.flatMap(team => team.players || []);
      setPlayers(allPlayers);
    } catch (err) {
      console.error('Failed to fetch players:', err);
    }
  };

  const handleInviteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.profile?.clubId) return;

    try {
      setInviteSubmitting(true);
      setError(null);

      const invitationData: any = {
        firstName: inviteForm.firstName,
        lastName: inviteForm.lastName,
        email: inviteForm.email,
        role: inviteRole
      };

      // Add linkedPlayerId for parent invitations
      if (inviteRole === ClubRole.PARENT) {
        if (!inviteForm.linkedPlayerId) {
          setError('Please select a player to link to this parent');
          setInviteSubmitting(false);
          return;
        }
        invitationData.linkedPlayerId = inviteForm.linkedPlayerId;
      }

      await clubService.createInvitation(user.profile.clubId, invitationData);
      setInviteSuccess(true);
      setInviteForm({ firstName: '', lastName: '', email: '', linkedPlayerId: '' });
      
      // Refresh invitations list
      fetchContacts();
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowInviteModal(false);
        setInviteSuccess(false);
      }, 2000);
    } catch (err: any) {
      console.error('Failed to send invitation:', err);
      setError(err.response?.data?.error?.message || 'Failed to send invitation');
    } finally {
      setInviteSubmitting(false);
    }
  };

  const formatRole = (role: ClubRole): string => {
    switch (role) {
      case ClubRole.CLUB_ADMIN:
        return 'Admin';
      case ClubRole.COACH:
        return 'Coach';
      case ClubRole.PARENT:
        return 'Parent';
      case ClubRole.DIRECTOR_OF_FOOTBALL:
        return 'Director of Football';
      default:
        return role;
    }
  };

  const getContactsByRole = (role: ClubRole) => {
    return contacts.filter(c => c.role === role);
  };

  const getRoleCount = (role: RoleFilter): number => {
    if (role === 'ALL') return contacts.length;
    return getContactsByRole(role).length;
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Contacts">
        <LoadingSpinner />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Contacts">
      <PageHeader>
        <PageTitle>Club Contacts</PageTitle>
        <InviteButtonGroup>
          <InviteButton onClick={() => handleInviteClick(ClubRole.COACH)}>
            Invite Coach
          </InviteButton>
          <InviteButton onClick={() => handleInviteClick(ClubRole.PARENT)}>
            Invite Parent
          </InviteButton>
          <InviteButton onClick={() => handleInviteClick(ClubRole.DIRECTOR_OF_FOOTBALL)}>
            Invite Director
          </InviteButton>
          <InviteButton onClick={() => handleInviteClick(ClubRole.CLUB_ADMIN)}>
            Invite Admin
          </InviteButton>
        </InviteButtonGroup>
      </PageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <FilterSection>
        <TabContainer>
          <Tab 
            $active={roleFilter === 'ALL'} 
            onClick={() => setRoleFilter('ALL')}
          >
            All ({getRoleCount('ALL')})
          </Tab>
          <Tab 
            $active={roleFilter === ClubRole.COACH} 
            onClick={() => setRoleFilter(ClubRole.COACH)}
          >
            Coaches ({getRoleCount(ClubRole.COACH)})
          </Tab>
          <Tab 
            $active={roleFilter === ClubRole.DIRECTOR_OF_FOOTBALL} 
            onClick={() => setRoleFilter(ClubRole.DIRECTOR_OF_FOOTBALL)}
          >
            Directors ({getRoleCount(ClubRole.DIRECTOR_OF_FOOTBALL)})
          </Tab>
          <Tab 
            $active={roleFilter === ClubRole.PARENT} 
            onClick={() => setRoleFilter(ClubRole.PARENT)}
          >
            Parents ({getRoleCount(ClubRole.PARENT)})
          </Tab>
          <Tab 
            $active={roleFilter === ClubRole.CLUB_ADMIN} 
            onClick={() => setRoleFilter(ClubRole.CLUB_ADMIN)}
          >
            Admins ({getRoleCount(ClubRole.CLUB_ADMIN)})
          </Tab>
        </TabContainer>

        <SearchBar
          type="text"
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FilterSection>

      <ContactsCard>
        <ContactsHeader>
          <ContactsTitle>Active Members</ContactsTitle>
          <ContactCount>{contacts.length} contact{contacts.length !== 1 ? 's' : ''}</ContactCount>
        </ContactsHeader>

        {contacts.length === 0 ? (
          <EmptyState>
            {searchTerm ? 'No contacts match your search.' : 'No contacts found. Invite members to get started.'}
          </EmptyState>
        ) : (
          <ContactsList>
            {contacts.map(contact => (
              <ContactItem key={contact.userId}>
                <ContactInfo>
                  <ContactName>{contact.firstName} {contact.lastName}</ContactName>
                  <ContactEmail>{contact.email}</ContactEmail>
                  <ContactMeta>
                    <ContactRole>{formatRole(contact.role)}</ContactRole>
                    {contact.associatedTeamName && (
                      <> • Team: {contact.associatedTeamName}</>
                    )}
                    {contact.associatedPlayerName && (
                      <> • Player: {contact.associatedPlayerName}</>
                    )}
                  </ContactMeta>
                </ContactInfo>
                <RemoveButton onClick={() => handleRemoveClick(contact)}>
                  Remove
                </RemoveButton>
              </ContactItem>
            ))}
          </ContactsList>
        )}
      </ContactsCard>

      {invitations.length > 0 && (
        <PendingInvitationsSection>
          <ContactsCard>
            <ContactsHeader>
              <ContactsTitle>Pending Invitations</ContactsTitle>
              <ContactCount>{invitations.length} pending</ContactCount>
            </ContactsHeader>
            <ContactsList>
              {invitations.map(invitation => (
                <InvitationItem key={invitation.id}>
                  <ContactInfo>
                    <ContactName>{invitation.invitedFirstName} {invitation.invitedLastName}</ContactName>
                    <ContactEmail>{invitation.invitedEmail}</ContactEmail>
                    <ContactMeta>
                      <ContactRole>{formatRole(invitation.invitedRole)}</ContactRole>
                      {' • '}
                      <InvitationStatus $status={invitation.status}>
                        {invitation.status}
                      </InvitationStatus>
                      {' • Expires: '}
                      {new Date(invitation.expiresAt).toLocaleDateString()}
                    </ContactMeta>
                  </ContactInfo>
                  <RemoveButton onClick={async () => {
                    if (!user?.profile?.clubId) return;
                    try {
                      await clubService.revokeInvitation(user.profile.clubId, invitation.id);
                      fetchContacts();
                    } catch (err: any) {
                      setError(err.response?.data?.error?.message || 'Failed to revoke invitation');
                    }
                  }}>
                    Revoke
                  </RemoveButton>
                </InvitationItem>
              ))}
            </ContactsList>
          </ContactsCard>
        </PendingInvitationsSection>
      )}

      {/* Remove Confirmation Modal */}
      <Modal $show={showRemoveModal} onClick={() => setShowRemoveModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Remove Member</ModalTitle>
          <p>
            Are you sure you want to remove <strong>{contactToRemove?.firstName} {contactToRemove?.lastName}</strong> from the club?
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: 'var(--spacing-sm)' }}>
            They will lose access to all club data but their account will be preserved for potential re-invitation.
          </p>
          <ModalButtons>
            <ModalButton $variant="secondary" onClick={() => setShowRemoveModal(false)}>
              Cancel
            </ModalButton>
            <ModalButton $variant="danger" onClick={handleRemoveConfirm}>
              Remove
            </ModalButton>
          </ModalButtons>
        </ModalContent>
      </Modal>

      {/* Invite Modal */}
      <Modal $show={showInviteModal} onClick={() => !inviteSubmitting && setShowInviteModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalTitle>Invite {formatRole(inviteRole)}</ModalTitle>
          
          {inviteSuccess && (
            <SuccessMessage>
              ✓ Invitation sent successfully! An email has been sent to {inviteForm.email}.
            </SuccessMessage>
          )}

          <form onSubmit={handleInviteSubmit}>
            <FormGroup>
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                type="text"
                value={inviteForm.firstName}
                onChange={(e) => setInviteForm({ ...inviteForm, firstName: e.target.value })}
                required
                disabled={inviteSubmitting}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                type="text"
                value={inviteForm.lastName}
                onChange={(e) => setInviteForm({ ...inviteForm, lastName: e.target.value })}
                required
                disabled={inviteSubmitting}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                disabled={inviteSubmitting}
              />
              <HelperText>
                An invitation email will be sent to this address with a secure link to join the club.
              </HelperText>
            </FormGroup>

            {inviteRole === ClubRole.PARENT && (
              <FormGroup>
                <Label htmlFor="linkedPlayer">Link to Player *</Label>
                <Select
                  id="linkedPlayer"
                  value={inviteForm.linkedPlayerId}
                  onChange={(e) => setInviteForm({ ...inviteForm, linkedPlayerId: e.target.value })}
                  required
                  disabled={inviteSubmitting}
                >
                  <option value="">Select a player...</option>
                  {players.map(player => (
                    <option key={player.id} value={player.id}>
                      {player.firstName} {player.lastName}
                      {player.teamName && ` (${player.teamName})`}
                    </option>
                  ))}
                </Select>
                <HelperText>
                  The parent will have access to this player's assessments and reports.
                </HelperText>
              </FormGroup>
            )}

            <FormGroup>
              <Label>Role</Label>
              <Input
                type="text"
                value={formatRole(inviteRole)}
                disabled
              />
              <HelperText>
                {inviteRole === ClubRole.COACH && 'Coaches can create teams, add players, and manage assessments.'}
                {inviteRole === ClubRole.PARENT && 'Parents have read-only access to their linked player\'s data.'}
                {inviteRole === ClubRole.DIRECTOR_OF_FOOTBALL && 'Directors have read-only access to all club data.'}
                {inviteRole === ClubRole.CLUB_ADMIN && 'Admins have full access to manage the club.'}
              </HelperText>
            </FormGroup>

            <ModalButtons>
              <ModalButton 
                type="button"
                $variant="secondary" 
                onClick={() => setShowInviteModal(false)}
                disabled={inviteSubmitting}
              >
                Cancel
              </ModalButton>
              <ModalButton 
                type="submit"
                $variant="primary"
                disabled={inviteSubmitting}
              >
                {inviteSubmitting ? 'Sending...' : 'Send Invitation'}
              </ModalButton>
            </ModalButtons>
          </form>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
};
